import {
  useEffect,
  useState,
  forwardRef,
  createContext,
  useContext,
} from "react";

import {
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxOutline,
} from "@mui/icons-material";
import { Autocomplete, TextField, Checkbox } from "@mui/material";

import PropTypes from "prop-types";

import { CustomChip } from "components";

import { useDebounce } from "hooks";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxOutline fontSize="small" />;

const ListboxContext = createContext({});

const ListboxComponent = forwardRef(({ children, ...listboxProps }, ref) => {
  const { selectAll, inputValue, handleSelectAll, allSelected } =
    useContext(ListboxContext);

  return (
    <ul {...listboxProps} ref={ref}>
      {selectAll && !inputValue && (
        <li
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSelectAll}
          style={{
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 8,
            paddingRight: 8,
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={allSelected}
            sx={{ padding: 0, margin: 0, marginRight: "4px" }}
          />
          <span style={{ margin: 0, padding: 0, lineHeight: 1.2 }}>
            Select All
          </span>
        </li>
      )}
      {children}
    </ul>
  );
});

const MultiSelectSearchDropdown = ({
  value = [],
  onChange,
  onSearch,
  label,
  options = [],
  sx,
  setIsDirty,
  selectAll = false,
  loading = false,
  ...rest
}) => {
  const [selectedValue, setSelectedValue] = useState(value || []);
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue, 500);

  useEffect(() => {
    setSelectedValue(value || []);
  }, [value]);

  useEffect(() => {
    if (debouncedSearch?.trim()) {
      onSearch?.(debouncedSearch);
    }
  }, [debouncedSearch]);

  const allSelected =
    options.length > 0 && selectedValue.length === options.length;

  const handleSelectAll = () => {
    const newValue = allSelected ? [] : options;
    setSelectedValue(newValue);
    setIsDirty?.(newValue.length > 0);
    onChange?.(newValue);
  };

  const handleChange = (_event, list) => {
    setSelectedValue(list);
    setIsDirty?.(list.length > 0);
    onChange?.(list);
  };

  return (
    <ListboxContext.Provider
      value={{ selectAll, inputValue, handleSelectAll, allSelected }}
    >
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={options}
        value={selectedValue}
        noOptionsText={loading ? "Loading..." : "No Options Found"}
        onChange={handleChange}
        onClose={() => {
          setInputValue("");
          onSearch?.("");
        }}
        onInputChange={(_e, val) => {
          setInputValue(val);
        }}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        renderOption={(props, option, { selected }) => {
          return (
            <li
              {...props}
              style={{
                paddingTop: 6,
                paddingBottom: 6,
                paddingLeft: 8,
                paddingRight: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                sx={{ padding: 0, margin: 0, marginRight: "4px" }}
              />
              <span style={{ margin: 0, padding: 0, lineHeight: 1.2 }}>
                {option}
              </span>
            </li>
          );
        }}
        ListboxComponent={ListboxComponent}
        disableClearable
        renderTags={(value, getTagProps) => {
          const maxShow = 1;

          return (
            <>
              {value.slice(0, maxShow).map((option, index) => (
                <CustomChip label={option} {...getTagProps({ index })} />
              ))}

              {value.length > maxShow && (
                <CustomChip label={`+${value.length - maxShow}`} />
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={`Search ${label}`}
          />
        )}
        sx={{
          ".MuiAutocomplete-tag": {
            maxWidth: "calc(100% - 60px)",
          },
          ".MuiAutocomplete-input": {
            minWidth: "10px !important",
          },
          ...sx,
        }}
        {...rest}
      />
    </ListboxContext.Provider>
  );
};

MultiSelectSearchDropdown.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.array,
  sx: PropTypes.object,
  setIsDirty: PropTypes.func,
  selectAll: PropTypes.bool,
};

export default MultiSelectSearchDropdown;
